import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import QRCode from "qrcode";
import { useAuth, type AuthTeam } from "../../auth/index.ts";
import { StatusPill } from "../../auth/Pills.tsx";
import { Drawer } from "../../auth/Drawer.tsx";
import { ApiError } from "../../lib/api.ts";
import { RevealInput } from "../../auth/RevealInput.tsx";
import { BrandAvatar } from "../../auth/BrandAvatar.tsx";
import {
  beginMfaEnrolment,
  completePasswordChange,
  confirmMfaEnrolment,
  getSecondFactorState,
  isWebAuthnSupported,
  listPasskeys,
  registerPasskey,
  removePasskey,
  renamePasskey,
  setActiveSecondFactor,
  startPasswordChange,
  updateDisplayName,
  unenrolMfa,
  type ActiveSecondFactor,
  type MfaEnrolChallenge,
  type Passkey,
  type SecondFactorState,
} from "../../lib/account-client.ts";
import { LicenseCard } from "./LicenseCard.tsx";
import { OtpInput } from "../../components/OtpInput.tsx";
import "./account.css";

/**
 * The operator's PROFILE / ACCOUNT screen (controlplane-auth task 13.1; requirements A5.3). It echoes
 * AgentDNA's `ProfilePage` composition — the signed-in identity, the team context, and the account
 * affordances — in the OATHERA navy/blue theme, but keeps THIS control plane's SERVER-SIDE SESSION
 * model: every field comes from {@link useAuth} (which derives from the server session), never from a
 * decoded token, and every action is a server round-trip.
 *
 * What it shows / does (A5.3 "sign out, switch team, change own credential"):
 *   - IDENTITY — the operator's email and display name. The opaque `user.id` is NEVER shown as a
 *     login handle (design §Data Models: `sub` is opaque, never an email); it is only offered as a
 *     copyable support reference, clearly labelled.
 *   - TEAM + ROLE — the currently selected team and the role resolved for it, as a text-first pill.
 *   - SWITCH TEAM — when the operator belongs to more than one team, a selector drives
 *     `useAuth().switchTeam`, which POSTs `/auth/switch-team`; the server rotates the session and
 *     re-resolves `(tid, role)`, and the context re-reads — the UI follows the server (A3.4).
 *   - SIGN OUT — `useAuth().signOut` → `POST /auth/logout`; the session is revoked server-side.
 *   - CHANGE PASSWORD — triggers the verified-email reset flow (`POST /auth/forgot` for the
 *     operator's own email); no plaintext is posted here.
 *   - MFA — enrol TOTP (begin → show secret → confirm with a code → show one-time recovery codes) or
 *     disable it. The secret stays server-side behind an opaque `secretRef`.
 *
 * It is rendered inside `className="oa-auth"` so the theme tokens resolve, and is keyboard- and
 * screen-reader-operable (labelled regions, a `role="status"`/`role="alert"` for async feedback, the
 * shared accessible `Modal` for the enrol dialog).
 */

/** The async feedback a profile action surfaces inline. */
type Feedback =
  | { readonly kind: "idle" }
  | { readonly kind: "busy" }
  | { readonly kind: "ok"; readonly message: string }
  | { readonly kind: "error"; readonly message: string };

function friendlyError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.kind === "waking") return "The control plane is waking up. Please try again in a moment.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
    if (error.kind === "forbidden") return "You are not permitted to do that.";
    if (error.kind === "unauthorized") return "That code was not recognized or has expired. Request a new one.";
    if (error.kind === "malformed") return "That action is not available yet. Please try again later.";
  }
  return fallback;
}

export function ProfilePage(): ReactNode {
  const auth = useAuth();
  const identity = auth.identity;

  if (identity === null) {
    // The shell only routes here behind ProtectedRoute, so an authenticated session is expected; a
    // null identity means the session read is still settling. Show an honest status, not a blank.
    return (
      <section className="oa-auth oa-account" aria-label="Account">
        <div className="oa-account__state" role="status" aria-live="polite">
          Loading your account…
        </div>
      </section>
    );
  }

  const { user, team, role } = identity;
  const displayName = user.displayName !== undefined && user.displayName.length > 0
    ? user.displayName
    : user.email;

  return (
    <section className="oa-auth oa-account" aria-label="Account">
      <header className="oa-account__header">
        <h1 className="oa-account__title">Account</h1>
        <p className="oa-account__subtitle">
          Your profile, team and sign-in settings for the OATHERA control plane.
        </p>
      </header>

      <div className="oa-account__profile-row">
        <IdentityCard
          displayName={displayName}
          email={user.email}
          userId={user.id}
          team={team}
          role={role}
          onNameSaved={auth.refresh}
        />
        <CredentialCard email={user.email} />
      </div>

      {/* Development License_Key (license-key-cards 14.4): the User_Key card on the left, a short
          explanatory card on the right. Rendered for every signed-in operator (a viewer gets the
          card's read-only guidance state); follows the Selected_Team via LicenseCard itself. Stacks
          into one column at <= 760px (see account.css). */}
      <div className="oa-account__license-row">
        <LicenseCard scope="user" />
        <article className="oa-account__card oa-account__license-note" aria-labelledby="oa-acct-license-note">
          <h2 className="oa-account__card-title" id="oa-acct-license-note">
            About development keys
          </h2>
          <p className="oa-account__card-note">
            Use this key to connect development and test setups. It is not for production use.
          </p>
          {/* 90 = USER_VALIDITY_DAYS in apps/ui/src/worker/license/types.ts. */}
          <p className="oa-account__card-note">
            Each development key is valid for 90 days. Regenerate it before it expires so your
            connections keep working.
          </p>
          <p className="oa-account__card-note">
            The Identity MCP and the Gateway MCP will require a valid license key to connect to the
            control plane.
          </p>
        </article>
      </div>

      <AccountSecurityCard />

      <SignOutCard signingOut={auth.signingOut} onSignOut={auth.signOut} />
    </section>
  );
}

// -------------------------------------------------------------------------------------------------
// Identity
// -------------------------------------------------------------------------------------------------

function IdentityCard(props: {
  displayName: string;
  email: string;
  userId: string;
  team: AuthTeam | null;
  role: string | null;
  onNameSaved: () => void | Promise<void>;
}): ReactNode {
  const nameInputId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.displayName);
  const [feedback, setFeedback] = useState<Feedback>({ kind: "idle" });

  const save = useCallback(async () => {
    setFeedback({ kind: "busy" });
    try {
      await updateDisplayName(draft.trim());
      setFeedback({ kind: "ok", message: "Name updated." });
      setEditing(false);
      await props.onNameSaved();
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Could not update your name.") });
    }
  }, [draft, props]);

  return (
    <article className="oa-account__card" aria-labelledby="oa-acct-identity">
      <h2 className="oa-account__card-title" id="oa-acct-identity">
        Profile
      </h2>

      <div className="oa-account__profile-head">
        <span className="oa-account__avatar oa-account__avatar--brand" aria-hidden="true"><BrandAvatar seed={(props.displayName || props.email || "operator")} size={56} /></span>
        <div>
          <div className="oa-account__profile-name">{props.displayName}</div>
          <div className="oa-account__member-email">{props.email}</div>
        </div>
      </div>

      <dl className="oa-account__facts">
        <div className="oa-account__fact">
          <dt>Name</dt>
          <dd>
            {editing ? (
              <span className="oa-account__input-reveal" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  id={nameInputId}
                  className="oa-account__input"
                  type="text"
                  maxLength={128}
                  value={draft}
                  autoFocus
                  aria-label="Your name"
                  disabled={feedback.kind === "busy"}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void save(); if (e.key === "Escape") { setEditing(false); setDraft(props.displayName); } }}
                />
                <button type="button" className="oa-account__btn" disabled={feedback.kind === "busy"} onClick={() => void save()}>Save</button>
                <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={feedback.kind === "busy"} onClick={() => { setEditing(false); setDraft(props.displayName); }}>Cancel</button>
              </span>
            ) : (
              <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
                {props.displayName}
                <button type="button" className="oa-account__btn oa-account__btn--ghost" onClick={() => { setDraft(props.displayName); setFeedback({ kind: "idle" }); setEditing(true); }}>Edit</button>
              </span>
            )}
          </dd>
        </div>
        <div className="oa-account__fact">
          <dt>Email</dt>
          <dd>{props.email}</dd>
        </div>
        <div className="oa-account__fact">
          <dt>Current team</dt>
          <dd>
            {props.team === null ? (
              <span className="oa-account__muted">No team selected</span>
            ) : (
              <>
                {props.team.name ?? props.team.id}{" "}
                {props.role !== null && (
                  <StatusPill tone={props.role === "admin" ? "accent" : "neutral"}>
                    {props.role}
                  </StatusPill>
                )}
              </>
            )}
          </dd>
        </div>
        <div className="oa-account__fact">
          <dt>Support reference</dt>
          {/* The opaque sub, offered ONLY as a support id — never as a login handle. */}
          <dd>
            <code className="oa-account__ref">{props.userId}</code>
          </dd>
        </div>
      </dl>
      {feedback.kind === "ok" && <p className="oa-account__feedback" data-kind="ok" role="status">{feedback.message}</p>}
      {feedback.kind === "error" && <p className="oa-account__feedback" data-kind="error" role="alert">{feedback.message}</p>}
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// Change password (verified-email reset flow)
// -------------------------------------------------------------------------------------------------

function CredentialCard(props: { email: string }): ReactNode {
  // The card itself is compact (a button); the whole flow runs in the RIGHT FLYOUT DRAWER so it does
  // not expand the card in place. "request" sends the emailed code; "verify" takes the code + the new
  // password; "done" is the success terminal.
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<"request" | "verify" | "done">("request");
  const [feedback, setFeedback] = useState<Feedback>({ kind: "idle" });
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // The reset code is valid 15 min server-side; use a slightly tighter client window (14 min) so a
  // reopened drawer never lands on verify with a code the server will reject moments later.
  const RESET_WINDOW_MS = 14 * 60 * 1000;
  // Persist WHEN the emailed reset code was last requested (epoch ms) in localStorage, keyed per
  // email, so resuming the code+password step survives a drawer close, a full page RELOAD, and
  // navigating away and back — not just React state (which a reload would drop). Keyed by email so
  // one account's pending reset never resumes under another. The stored value is only a timestamp
  // (not the code), so nothing sensitive is persisted.
  const storageKey = `oa.pwreset.sentAt.${props.email.trim().toLowerCase()}`;
  const readSentAt = useCallback((): number | null => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return null;
      const t = Number(raw);
      if (!Number.isFinite(t)) return null;
      // Expired entries are swept so a stale key never resumes verify after the window.
      if (Date.now() - t >= RESET_WINDOW_MS) {
        window.localStorage.removeItem(storageKey);
        return null;
      }
      return t;
    } catch {
      return null;
    }
  }, [storageKey, RESET_WINDOW_MS]);
  const writeSentAt = useCallback((t: number | null) => {
    try {
      if (t === null) window.localStorage.removeItem(storageKey);
      else window.localStorage.setItem(storageKey, String(t));
    } catch {
      /* storage unavailable (private mode / disabled) — fall back to in-memory state only */
    }
  }, [storageKey]);

  // Seed from storage so a reload/return resumes correctly; keep a state mirror for reactivity.
  const [codeSentAt, setCodeSentAtState] = useState<number | null>(() => readSentAt());
  const setCodeSentAt = useCallback((t: number | null) => {
    writeSentAt(t);
    setCodeSentAtState(t);
  }, [writeSentAt]);

  const codeId = useId();
  const pwId = useId();
  const confirmId = useId();


  // Clear the editable fields (NOT codeSentAt, so a reopened drawer can resume on verify).
  const clearFields = useCallback(() => {
    setFeedback({ kind: "idle" });
    setCode("");
    setNewPassword("");
    setConfirm("");
  }, []);

  // Decide which stage to open on: resume `verify` when a still-valid code was already emailed,
  // otherwise start at `request`. Called by the "Change password" button.
  const openDrawer = useCallback(() => {
    clearFields();
    // Re-read from storage at CLICK time (not the render-time snapshot) so a code that expired while
    // the page sat idle correctly falls back to the request step. When storage is unavailable
    // (private mode), fall back to the in-memory mirror so a resume still works within the session.
    const sentAt = readSentAt() ?? codeSentAt;
    setCodeSentAtState(sentAt);
    setStage(sentAt !== null ? "verify" : "request");
    setOpen(true);
  }, [clearFields, readSentAt, codeSentAt]);

  const requestCode = useCallback(async () => {
    setFeedback({ kind: "busy" });
    try {
      await startPasswordChange(props.email);
      setCodeSentAt(Date.now());
      setStage("verify");
      setFeedback({
        kind: "ok",
        message: "If a password is set for your account, a single-use code is on its way to your email. Enter it below with your new password.",
      });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Could not start a password change.") });
    }
  }, [props.email]);

  const submitNewPassword = useCallback(async () => {
    if (code.trim().length === 0 || newPassword.length === 0) {
      setFeedback({ kind: "error", message: "Enter the emailed code and a new password." });
      return;
    }
    if (newPassword !== confirm) {
      setFeedback({ kind: "error", message: "The two passwords do not match." });
      return;
    }
    setFeedback({ kind: "busy" });
    try {
      await completePasswordChange(props.email, code.trim(), newPassword);
      setCode("");
      setNewPassword("");
      setConfirm("");
      setCodeSentAt(null);
      setStage("done");
      setFeedback({
        kind: "ok",
        message: "Your password has been changed. For your security all sessions were signed out. Please sign in again with your new password.",
      });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Could not change your password.") });
    }
  }, [props.email, code, newPassword, confirm]);

  return (
    <article className="oa-account__card" aria-labelledby="oa-acct-cred">
      <h2 className="oa-account__card-title" id="oa-acct-cred">
        Password
      </h2>
      <p className="oa-account__card-note">
        Change your password through a verified-email reset. We email a single-use code to{" "}
        <strong>{props.email}</strong>; you enter it with your new password in the panel that opens.
      </p>
      <div className="oa-account__btn-row" style={{ marginTop: "auto" }}>
        <button
          type="button"
          className="oa-account__btn"
          onClick={openDrawer}
        >
          Change password
        </button>
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Change password"
        footer={
          stage === "verify" ? (
            <>
              <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={feedback.kind === "busy"} onClick={() => void requestCode()}>
                Resend code
              </button>
              <button type="button" className="oa-account__btn" disabled={feedback.kind === "busy"} onClick={() => void submitNewPassword()}>
                {feedback.kind === "busy" ? "Changing…" : "Set new password"}
              </button>
            </>
          ) : stage === "request" ? (
            <button type="button" className="oa-account__btn" disabled={feedback.kind === "busy"} onClick={() => void requestCode()}>
              {feedback.kind === "busy" ? "Sending…" : "Email me a code"}
            </button>
          ) : (
            <button type="button" className="oa-account__btn" onClick={() => setOpen(false)}>
              Close
            </button>
          )
        }
      >
        <div className="oa-account__drawer-body">
          {stage === "request" && (
            <p className="oa-account__card-note">
              We will email a single-use code to <strong>{props.email}</strong>. Click the button
              below to send it, then enter the code with your new password.
            </p>
          )}

          {stage === "verify" && (
            <form
              className="oa-account__form"
              onSubmit={(e) => { e.preventDefault(); void submitNewPassword(); }}
            >
              <p className="oa-account__card-note">
                Enter the single-use code we emailed to <strong>{props.email}</strong> and choose a
                new password. If the code has expired, use <em>Resend code</em>.
              </p>
              <div className="oa-account__row">
                <label className="oa-account__label" htmlFor={codeId}>Emailed code</label>
                <OtpInput id={codeId} value={code} onChange={setCode} align="start" />
              </div>
              <div className="oa-account__row">
                <label className="oa-account__label" htmlFor={pwId}>New password</label>
                <RevealInput id={pwId} value={newPassword} onChange={setNewPassword} autoComplete="new-password" />
              </div>
              <div className="oa-account__row">
                <label className="oa-account__label" htmlFor={confirmId}>Confirm new password</label>
                <RevealInput id={confirmId} value={confirm} onChange={setConfirm} autoComplete="new-password" />
              </div>
              {/* A hidden submit keeps Enter working inside the form; the visible actions live in the footer. */}
              <button type="submit" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
            </form>
          )}

          <ActionFeedback feedback={feedback} />
        </div>
      </Drawer>
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// MFA (TOTP) enrol / disable
// -------------------------------------------------------------------------------------------------

/**
 * Render an `otpauth://` URI as a scannable QR code. The QR is generated client-side with the
 * pinned `qrcode` library into a data-URL `<img>` (CSP-safe: `img-src 'self' data:`, no inline
 * script, no third-party origin). When no URI is available (older backend) it renders nothing and
 * the operator falls back to the manual base32 secret shown alongside.
 */
function QrCode(props: { otpauthUri?: string | undefined; alt: string }): ReactNode {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const uri = props.otpauthUri;
  useEffect(() => {
    let cancelled = false;
    setDataUrl(null);
    setFailed(false);
    if (uri === undefined || uri === "") return;
    QRCode.toDataURL(uri, { errorCorrectionLevel: "M", margin: 1, width: 192 })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [uri]);

  if (uri === undefined || uri === "" || failed) return null;
  if (dataUrl === null) {
    return <div className="oa-account__qr" data-kind="loading" aria-hidden="true" />;
  }
  return <img className="oa-account__qr" src={dataUrl} alt={props.alt} width={192} height={192} />;
}

/** The props each security sub-panel receives from the parent card to render its enabled state. */
interface SecurityPanelProps {
  /** True when THIS factor has been enrolled (so it can be enabled). */
  readonly enrolled: boolean;
  /** True when THIS factor is the enabled (enforced) one. */
  readonly active: boolean;
  /** True when the OTHER factor is the enabled one (so this panel is dimmed). */
  readonly otherActive: boolean;
  /** True while a switch is in flight (disables the enable button). */
  readonly switching: boolean;
  /** Make THIS factor the enabled one. */
  readonly onEnable: () => void;
  /** Disable THIS factor (it is currently the enabled one). */
  readonly onDisable: () => void;
  /** Tell the parent to re-read the second-factor state (after enroll / register / remove). */
  readonly onChanged: () => void | Promise<void>;
}

/**
 * The "Account security" card: a parent panel holding two side-by-side sub-panels — the TOTP
 * two-factor control and the FIDO2 passkey ("Go Passwordless") control. The grid collapses to one
 * column on a narrow viewport.
 */
function AccountSecurityCard(): ReactNode {
  // TOTP and a passkey may both be enrolled, but only ONE is enabled (enforced) at a time. This
  // card owns that shared state and hands each sub-panel its enrolled/enabled status + an enable
  // action, so switching the enabled factor in one panel updates the badge/border in both.
  const auth = useAuth();
  // The selected team drives SSO (which provider, whether enforced); re-read when it changes so a
  // team switch updates this panel instead of showing the previous team's provider.
  const currentTeam = auth.identity?.team ?? null;
  const currentTeamId = currentTeam?.id ?? null;
  // The human label for the selected team, used to make the panel copy explicit that the enabled
  // sign-in factor it shows and changes governs THIS team only (Requirement 3.3). Falls back to the
  // opaque tenant id, then to a neutral phrase when no team is selected.
  const teamName = currentTeam?.name ?? currentTeam?.id ?? "your current team";
  const [state, setState] = useState<SecondFactorState | null>(null);
  const [switching, setSwitching] = useState(false);

  const reload = useCallback(async () => {
    try {
      setState(await getSecondFactorState());
    } catch {
      // A failed read leaves the panels usable (they still enroll/register); show no enabled badge.
      setState({
        totpEnrolled: false, passkeyCount: 0, active: "none",
        teamHasSso: false, teamSsoEnforced: false, isTeamAdmin: false,
        ssoLoginEnabled: false, ssoBlockedRootAdmin: false,
        providerId: null, providerLabel: null, providerLogoUrl: null,
      });
    }
  }, []);

  useEffect(() => {
    void reload();
    // currentTeamId in the deps so switching teams re-reads the new team's SSO/factor state.
  }, [reload, currentTeamId]);

  const enable = useCallback(
    async (factor: ActiveSecondFactor) => {
      setSwitching(true);
      try {
        await setActiveSecondFactor(factor);
        await reload();
      } catch {
        // Keep the current badge if the switch failed (e.g. the factor was not actually enrolled).
      } finally {
        setSwitching(false);
      }
    },
    [reload],
  );

  // Disable whichever factor is currently enabled: posting `none` clears the selected team's non-SSO
  // factor AND its SSO flag (server-side mutual exclusivity), so one primitive disables totp, passkey
  // or sso. The panel only surfaces this when a factor is actually enabled.
  const disable = useCallback(async () => {
    setSwitching(true);
    try {
      await setActiveSecondFactor("none");
      await reload();
    } catch {
      // Keep the current badge if the change failed.
    } finally {
      setSwitching(false);
    }
  }, [reload]);

  const active = state?.active ?? "none";
  const teamHasSso = state?.teamHasSso === true;
  const teamSsoEnforced = state?.teamSsoEnforced === true;
  const isTeamAdmin = state?.isTeamAdmin === true;
  const providerLogoUrl = state?.providerLogoUrl ?? null;

  // A NON-ADMIN member of an SSO-ENFORCED team sees ONLY a full-width "SSO enabled" panel — their
  // sign-in is governed by the team's provider, so TOTP/passkey are not theirs to choose.
  if (state !== null && teamHasSso && teamSsoEnforced && !isTeamAdmin) {
    // SSO is the ONLY option here (enforced by the team) and is on by default. Render the SSO panel
    // full width as a read-only, always-enabled panel.
    return (
      <article className="oa-account__card" aria-labelledby="oa-acct-security">
        <h2 className="oa-account__card-title" id="oa-acct-security">
          Sign-in for {teamName}
        </h2>
        <p className="oa-account__card-note">
          This sign-in method applies to <strong>{teamName}</strong>. Each team you belong to keeps
          its own setting, so changing it here does not affect your other teams.
        </p>
        <div className="oa-account__security-grid">
          <SsoFactorPanel
            active
            readOnly
            fullWidth
            otherActive={false}
            switching={false}
            isTeamAdmin={false}
            providerLabel={state.providerLabel}
            providerLogoUrl={providerLogoUrl}
            onEnable={() => {}}
          />
        </div>
      </article>
    );
  }

  // Admins (and members of a team without enforced SSO) get the factor options. SSO is a THIRD option
  // alongside TOTP and passkey when the team has an SSO provider configured. Only one is enabled.
  const showSso = teamHasSso;
  const otherActive = (mine: ActiveSecondFactor): boolean => active !== "none" && active !== mine;

  return (
    <article className="oa-account__card" aria-labelledby="oa-acct-security">
      <h2 className="oa-account__card-title" id="oa-acct-security">
        Sign-in for {teamName}
      </h2>
      <p className="oa-account__card-note">
        These settings govern how you sign in to <strong>{teamName}</strong>. Each team you belong to
        keeps its own sign-in method, so changing it here does not affect your other teams. Use an
        authenticator app, a passkey{showSso ? ", or your team's single sign-on" : ""} as your second
        factor — only one at a time. You can set up more than one, but only the enabled one is
        required when you sign in to this team.
      </p>
      <div className="oa-account__security-grid">
        <TotpPanel
          enrolled={state?.totpEnrolled === true}
          active={active === "totp"}
          otherActive={otherActive("totp")}
          switching={switching}
          onEnable={() => void enable("totp")}
          onDisable={() => void disable()}
          onChanged={reload}
        />
        <PasskeyPanel
          enrolled={(state?.passkeyCount ?? 0) > 0}
          active={active === "passkey" && (state?.passkeyCount ?? 0) > 0}
          otherActive={otherActive("passkey")}
          switching={switching}
          onEnable={() => void enable("passkey")}
          onDisable={() => void disable()}
          onChanged={reload}
        />
        {showSso && (
          <SsoFactorPanel
            active={active === "sso"}
            otherActive={otherActive("sso")}
            switching={switching}
            isTeamAdmin={isTeamAdmin}
            blockedRootAdmin={state?.ssoBlockedRootAdmin === true}
            providerLabel={state?.providerLabel ?? null}
            providerLogoUrl={providerLogoUrl}
            onEnable={() => void enable("sso")}
            onDisable={() => void disable()}
          />
        )}
      </div>
    </article>
  );
}

/**
 * The SSO sub-panel — the THIRD account-security option, shown when the team has an SSO provider.
 * Enabling it makes this account sign in through the team's provider (mutually exclusive with
 * TOTP/passkey). Because an admin enabling SSO for themselves risks a lockout if the IdP breaks, a
 * confirmation warning is shown before the enable goes through.
 */
function SsoFactorPanel(props: {
  readonly active: boolean;
  readonly otherActive: boolean;
  readonly switching: boolean;
  readonly isTeamAdmin: boolean;
  readonly providerLabel: string | null;
  readonly providerLogoUrl: string | null;
  readonly onEnable: () => void;
  /** Disable SSO (only offered when SSO is enabled AND not team-enforced). */
  readonly onDisable?: () => void;
  /** Read-only, always-enabled rendering (an enforced member who cannot change the factor). */
  readonly readOnly?: boolean;
  /** Span the full card width (used when SSO is the only option). */
  readonly fullWidth?: boolean;
  /**
   * The signed-in user is a ROOT-org admin: SSO as a sign-in factor is blocked to avoid locking the
   * root org's administrators out if the IdP changes. The enable control is disabled with an
   * explanation; a root-org NON-admin never receives this.
   */
  readonly blockedRootAdmin?: boolean;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const label = props.providerLabel ?? "your identity provider";
  const readOnly = props.readOnly === true;
  const blocked = props.blockedRootAdmin === true && !props.active;
  return (
    <section
      className={props.fullWidth === true ? "oa-account__subpanel oa-account__subpanel--full" : "oa-account__subpanel"}
      data-enabled={props.active ? "true" : undefined}
      data-dimmed={props.otherActive ? "true" : undefined}
      aria-labelledby="oa-acct-sso"
    >
      {props.active && <EnabledBadge />}
      <h3 className="oa-account__subpanel-title" id="oa-acct-sso">
        Single sign-on
      </h3>
      <div className="oa-account__sso-logo">
        {props.providerLogoUrl !== null ? (
          <img src={props.providerLogoUrl} alt={label} width={48} height={48} />
        ) : (
          <span className="oa-account__sso-logo-fallback" aria-hidden="true">SSO</span>
        )}
        <span className="oa-account__sso-provider">{label}</span>
      </div>
      <p className="oa-account__card-note">
        Single sign-on lets you sign in with your organization&rsquo;s identity provider instead of a
        separate password or code. When enabled, you&rsquo;ll be redirected to {label} to authenticate.
      </p>

      {blocked ? (
        <p className="oa-account__enable-row" data-kind="info">
          Single sign-on isn&rsquo;t available as your sign-in factor because you administer the root
          organization. This prevents an identity-provider change from locking the root org&rsquo;s
          administrators out. Keep a password, passkey, or authenticator app as your factor.
        </p>
      ) : readOnly ? (
        <p className="oa-account__enable-row">
          Your team requires single sign-on. Sign in from the login page using single sign-on.
        </p>
      ) : props.active ? (
        <div className="oa-account__enable-row">
          <span>This is your enabled sign-in method.</span>
          <button
            type="button"
            className="oa-account__btn oa-account__btn--ghost"
            disabled={props.switching}
            onClick={() => props.onDisable?.()}
          >
            Disable this factor
          </button>
        </div>
      ) : confirming ? (
        <div className="oa-account__enable-row" style={{ flexDirection: "column", alignItems: "stretch", gap: "8px" }}>
          <p className="oa-account__feedback" data-kind="error" role="alert" style={{ margin: 0 }}>
            {props.isTeamAdmin
              ? "Warning: enabling SSO for your admin account means you can only sign in through your identity provider. If it is misconfigured or unavailable, you could be locked out of administering this team. Make sure SSO works before enabling it here."
              : "Enabling SSO means you sign in through your identity provider."}
          </p>
          <div className="oa-account__btn-row">
            <button type="button" className="oa-account__btn" disabled={props.switching} onClick={() => { setConfirming(false); props.onEnable(); }}>
              {props.isTeamAdmin ? "I understand, enable SSO" : "Enable SSO"}
            </button>
            <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={props.switching} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="oa-account__enable-row">
          <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={props.switching} onClick={() => setConfirming(true)}>
            Enable this factor
          </button>
        </div>
      )}
    </section>
  );
}

/** The checkmark "Enabled" badge pinned to a sub-panel's top-right corner when it is the active factor. */
function EnabledBadge(): ReactNode {
  return (
    <span className="oa-account__subpanel-badge">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      Enabled
    </span>
  );
}

/**
 * The enable control shown under a sub-panel's actions. When the factor is active it reads "Enabled";
 * when it is enrolled but NOT active it offers an "Enable" button that makes it the enforced factor
 * (which disables the other); when it is not yet enrolled it prompts to set it up first.
 */
function EnableControl(props: {
  readonly enrolled: boolean;
  readonly active: boolean;
  readonly switching: boolean;
  readonly onEnable: () => void;
  readonly onDisable: () => void;
}): ReactNode {
  if (!props.enrolled) {
    return <p className="oa-account__enable-row">Set this up to enable it as your second factor.</p>;
  }
  // When THIS factor is enabled, offer to turn it off (visible only while enabled, per requirement).
  if (props.active) {
    return (
      <div className="oa-account__enable-row">
        <span>This is your enabled second factor.</span>
        <button
          type="button"
          className="oa-account__btn oa-account__btn--ghost"
          disabled={props.switching}
          onClick={props.onDisable}
        >
          Disable this factor
        </button>
      </div>
    );
  }
  return (
    <div className="oa-account__enable-row">
      <button
        type="button"
        className="oa-account__btn oa-account__btn--ghost"
        disabled={props.switching}
        onClick={props.onEnable}
      >
        Enable this factor
      </button>
    </div>
  );
}

/**
 * Map a passkey-REGISTRATION failure to a specific, honest message. `@simplewebauthn/browser`'s
 * `startRegistration` surfaces the browser WebAuthn ceremony errors; it throws a `WebAuthnError`
 * that WRAPS the underlying `DOMException` (so it is NOT itself a `DOMException`), carrying the
 * original in `.cause` and a stable `.name`. We classify by the WebAuthn error name on the error OR
 * its cause, so the user gets a real explanation instead of a blanket "could not register".
 *
 *   - NotAllowedError      → the user dismissed/timed-out the native prompt (a cancel; soft message).
 *   - InvalidStateError    → the chosen authenticator ALREADY holds a passkey for this account (it
 *                            is in `excludeCredentials`). This is the common "register a 2nd passkey
 *                            but pick the same device/sync account" case — explain it plainly.
 *   - a server `ApiError`  → defer to the shared `friendlyError` copy.
 *   - anything else        → the fallback, but include the error name so an unexpected ceremony
 *                            failure is diagnosable rather than opaque.
 */
function passkeyRegisterError(error: unknown): string {
  if (error instanceof ApiError) return friendlyError(error, "Could not register a passkey.");
  // The WebAuthn error name can be on the thrown error or on its wrapped cause.
  const name =
    (error instanceof Error && error.name) ||
    (error instanceof Error && error.cause instanceof Error && error.cause.name) ||
    (error instanceof DOMException && error.name) ||
    "";
  if (name === "NotAllowedError") return "Passkey registration was cancelled or timed out.";
  if (name === "InvalidStateError") {
    return "This device already has a passkey for your account. Use a different device, phone, or security key to add another.";
  }
  if (name === "SecurityError") {
    return "This site's security settings blocked passkey registration. Make sure you are on cp.oathera.net over HTTPS.";
  }
  if (name === "NotSupportedError") return "This device or browser does not support the requested passkey type.";
  // Unexpected: surface the error name so the real cause is visible (not a blanket failure).
  const detail = name !== "" ? ` (${name})` : "";
  return `Could not register a passkey${detail}. Please try a different device or try again.`;
}

/**
 * The "Go Passwordless" sub-panel: register a FIDO2 passkey (native platform authenticator, phone,
 * security key — anything the browser offers), list the operator's registered passkeys, and remove
 * one. A registered passkey can then be used to sign in without a password from the login screen.
 * The browser half of each ceremony runs through the pinned `@simplewebauthn/browser`; the server
 * verifies and persists. When the browser has no WebAuthn support the panel says so and offers no
 * affordance.
 */
function PasskeyPanel(props: SecurityPanelProps): ReactNode {
  const supported = isWebAuthnSupported();
  const [passkeys, setPasskeys] = useState<readonly Passkey[] | null>(null);
  const [feedback, setFeedback] = useState<Feedback>({ kind: "idle" });
  // The id of the passkey currently being renamed inline, and the draft label text.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState("");

  const refresh = useCallback(async () => {
    try {
      const list = await listPasskeys();
      setPasskeys(list);
    } catch {
      // A failed list leaves the panel usable (the operator can still try to register); show nothing.
      setPasskeys([]);
    }
  }, []);

  useEffect(() => {
    if (!supported) return;
    void refresh();
  }, [supported, refresh]);

  const onRegister = useCallback(async () => {
    setFeedback({ kind: "busy" });
    try {
      await registerPasskey();
      setFeedback({ kind: "ok", message: "Passkey registered. You can now sign in with it." });
      await refresh();
      await props.onChanged();
    } catch (error) {
      setFeedback({ kind: "error", message: passkeyRegisterError(error) });
    }
  }, [refresh, props]);

  const onRemove = useCallback(
    async (id: string) => {
      setFeedback({ kind: "busy" });
      try {
        await removePasskey(id);
        setFeedback({ kind: "ok", message: "Passkey removed." });
        await refresh();
        await props.onChanged();
      } catch (error) {
        setFeedback({ kind: "error", message: friendlyError(error, "Could not remove the passkey.") });
      }
    },
    [refresh, props],
  );

  const startRename = useCallback((pk: Passkey) => {
    setEditingId(pk.id);
    setDraftLabel(pk.label ?? passkeyName(pk));
  }, []);

  const cancelRename = useCallback(() => {
    setEditingId(null);
    setDraftLabel("");
  }, []);

  const saveRename = useCallback(
    async (id: string) => {
      setFeedback({ kind: "busy" });
      try {
        await renamePasskey(id, draftLabel.trim());
        setEditingId(null);
        setDraftLabel("");
        setFeedback({ kind: "ok", message: "Passkey renamed." });
        await refresh();
      } catch (error) {
        setFeedback({ kind: "error", message: friendlyError(error, "Could not rename the passkey.") });
      }
    },
    [draftLabel, refresh],
  );

  return (
    <section className="oa-account__subpanel" data-enabled={props.active ? "true" : undefined} data-dimmed={props.otherActive ? "true" : undefined} aria-labelledby="oa-acct-passkey">
      {props.active && <EnabledBadge />}
      <h3 className="oa-account__subpanel-title" id="oa-acct-passkey">
        Go passwordless
      </h3>
      <p className="oa-account__card-note">
        Register a passkey on this device, your phone, or a security key (any FIDO2 method), then
        sign in without a password.
      </p>

      {!supported ? (
        <p className="oa-account__muted">This browser does not support passkeys.</p>
      ) : (
        <>
          <div className="oa-account__btn-row">
            <button
              type="button"
              className="oa-account__btn"
              disabled={feedback.kind === "busy"}
              onClick={() => void onRegister()}
            >
              {feedback.kind === "busy" ? "Working…" : "Register a passkey"}
            </button>
          </div>

          {passkeys !== null && passkeys.length > 0 && (
            <ul className="oa-account__passkeys" aria-label="Registered passkeys">
              {passkeys.map((pk) =>
                editingId === pk.id ? (
                  <li key={pk.id} className="oa-account__passkey">
                    <input
                      className="oa-account__input"
                      type="text"
                      maxLength={64}
                      value={draftLabel}
                      autoFocus
                      aria-label="Passkey name"
                      onChange={(e) => setDraftLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void saveRename(pk.id);
                        if (e.key === "Escape") cancelRename();
                      }}
                    />
                    <span className="oa-account__passkey-actions">
                      <button
                        type="button"
                        className="oa-account__btn"
                        disabled={feedback.kind === "busy"}
                        onClick={() => void saveRename(pk.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="oa-account__btn oa-account__btn--ghost"
                        disabled={feedback.kind === "busy"}
                        onClick={cancelRename}
                      >
                        Cancel
                      </button>
                    </span>
                  </li>
                ) : (
                  <li key={pk.id} className="oa-account__passkey">
                    <span className="oa-account__passkey-meta">
                      <span className="oa-account__passkey-name">
                        {pk.label ?? passkeyName(pk)}
                      </span>
                      <span className="oa-account__passkey-sub">
                        {pk.backedUp ? "Synced passkey" : "Device-bound passkey"}
                        {pk.lastUsedAt !== null ? " · used recently" : ""}
                      </span>
                    </span>
                    <span className="oa-account__passkey-actions">
                      <button
                        type="button"
                        className="oa-account__btn oa-account__btn--ghost"
                        disabled={feedback.kind === "busy"}
                        onClick={() => startRename(pk)}
                        aria-label={`Rename passkey ${pk.label ?? passkeyName(pk)}`}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="oa-account__btn oa-account__btn--ghost"
                        disabled={feedback.kind === "busy"}
                        onClick={() => void onRemove(pk.id)}
                        aria-label={`Remove passkey ${pk.label ?? passkeyName(pk)}`}
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ),
              )}
            </ul>
          )}
          {passkeys !== null && passkeys.length === 0 && (
            <p className="oa-account__muted">No passkeys registered yet.</p>
          )}

          <ActionFeedback feedback={feedback} />

          <EnableControl
            enrolled={props.enrolled || (passkeys !== null && passkeys.length > 0)}
            active={props.active && (passkeys === null || passkeys.length > 0)}
            switching={props.switching}
            onEnable={props.onEnable}
            onDisable={props.onDisable}
          />
        </>
      )}
    </section>
  );
}

/** A friendly fallback name for a passkey that has no operator-set label. */
function passkeyName(pk: Passkey): string {
  if (pk.transports.includes("internal")) return "This device";
  if (pk.transports.includes("hybrid")) return "Phone or tablet";
  if (pk.transports.includes("usb") || pk.transports.includes("nfc")) return "Security key";
  return "Passkey";
}

function TotpPanel(props: SecurityPanelProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [challenge, setChallenge] = useState<MfaEnrolChallenge | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [feedback, setFeedback] = useState<Feedback>({ kind: "idle" });
  const codeId = useId();

  const beginEnrol = useCallback(async () => {
    setFeedback({ kind: "busy" });
    setRecoveryCodes(null);
    setCode("");
    try {
      const ch = await beginMfaEnrolment();
      setChallenge(ch);
      setOpen(true);
      setFeedback({ kind: "idle" });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Could not start MFA enrollment.") });
    }
  }, []);

  const confirmEnrol = useCallback(async () => {
    if (challenge === null || code.trim().length === 0) return;
    setFeedback({ kind: "busy" });
    try {
      const codes = await confirmMfaEnrolment(challenge.secretRef, code.trim());
      // Show the one-time recovery codes INSIDE the flyout; it stays open until the operator closes
      // it. Refresh the parent so the TOTP panel now shows as enrolled, but DO NOT enable yet —
      // enabling happens on close (and only when a passkey is not the enabled factor).
      setRecoveryCodes(codes);
      setFeedback({ kind: "ok", message: "Authenticator app set up. Save your recovery codes below." });
      await props.onChanged();
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "That code did not verify. Try again.") });
    }
  }, [challenge, code, props]);

  const disableMfa = useCallback(async () => {
    setFeedback({ kind: "busy" });
    try {
      await unenrolMfa();
      setFeedback({ kind: "ok", message: "Authenticator app removed." });
      await props.onChanged();
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Could not remove the authenticator.") });
    }
  }, [props]);

  const closeDialog = useCallback(() => {
    // If this close follows a successful enrolment (recovery codes were shown), enable TOTP as the
    // enforced factor — UNLESS a passkey is already the enabled factor, in which case we leave the
    // user's passwordless choice untouched and just close.
    if (recoveryCodes !== null && !props.otherActive) {
      props.onEnable();
    }
    setOpen(false);
    setChallenge(null);
    setCode("");
    setRecoveryCodes(null);
    setFeedback({ kind: "idle" });
  }, [recoveryCodes, props]);

  return (
    <section className="oa-account__subpanel" data-enabled={props.active ? "true" : undefined} data-dimmed={props.otherActive ? "true" : undefined} aria-labelledby="oa-acct-mfa">
      {props.active && <EnabledBadge />}
      <h3 className="oa-account__subpanel-title" id="oa-acct-mfa">
        Two-factor authentication
      </h3>
      <p className="oa-account__card-note">
        Protect your account with a time-based one-time code from an authenticator app.
      </p>
      <div className="oa-account__btn-row">
        <button
          type="button"
          className="oa-account__btn"
          disabled={feedback.kind === "busy"}
          onClick={() => void beginEnrol()}
        >
          {props.enrolled ? "Re-enroll" : "Set up authenticator"}
        </button>
        {props.enrolled && (
          <button
            type="button"
            className="oa-account__btn oa-account__btn--ghost"
            disabled={feedback.kind === "busy"}
            onClick={() => void disableMfa()}
          >
            Remove
          </button>
        )}
      </div>
      <ActionFeedback feedback={feedback} />

      <EnableControl
        enrolled={props.enrolled}
        active={props.active}
        switching={props.switching}
        onEnable={props.onEnable}
        onDisable={props.onDisable}
      />

      <Drawer
        open={open}
        onClose={closeDialog}
        title="Set up two-factor authentication"
        footer={
          recoveryCodes !== null ? (
            // After a successful verify we show the recovery codes; a single Close button finishes
            // (and enables TOTP on close unless a passkey is the enabled factor).
            <button type="button" className="oa-account__btn" onClick={closeDialog}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="oa-account__btn oa-account__btn--ghost" onClick={closeDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="oa-account__btn"
                disabled={feedback.kind === "busy" || code.trim().length === 0}
                onClick={() => void confirmEnrol()}
              >
                {feedback.kind === "busy"
                  ? "Verifying…"
                  : props.otherActive
                    ? "Verify"
                    : "Verify and enable"}
              </button>
            </>
          )
        }
      >
        {recoveryCodes !== null ? (
          <div className="oa-account__enrol">
            <p className="oa-account__card-note">
              Your authenticator app is set up. Save these one-time recovery codes somewhere safe —
              they are shown only once and let you sign in if you lose your authenticator.
            </p>
            <ul className="oa-account__codes" aria-label="Recovery codes">
              {recoveryCodes.map((rc) => (
                <li key={rc}>
                  <code>{rc}</code>
                </li>
              ))}
            </ul>
            {!props.otherActive && (
              <p className="oa-account__card-note">
                Closing this will enable two-factor authentication as your second factor.
              </p>
            )}
          </div>
        ) : challenge !== null ? (
          <div className="oa-account__enrol">
            <p className="oa-account__card-note">
              Scan this QR code with your authenticator app (1Kosmos, Google Authenticator,
              Microsoft Authenticator, 1Password, …):
            </p>
            <QrCode
              otpauthUri={challenge.otpauthUri}
              alt="TOTP enrollment QR code"
            />
            <p className="oa-account__card-note">
              Can&rsquo;t scan? Enter this secret manually instead:
            </p>
            <code className="oa-account__secret">{challenge.secret}</code>
            <div className="oa-account__row">
              <label className="oa-account__label" htmlFor={codeId}>
                Authentication code
              </label>
              <OtpInput id={codeId} value={code} onChange={setCode} align="start" />
            </div>
            <ActionFeedback feedback={feedback} />
          </div>
        ) : null}
      </Drawer>
    </section>
  );
}

// -------------------------------------------------------------------------------------------------
// Sign out
// -------------------------------------------------------------------------------------------------

function SignOutCard(props: { signingOut: boolean; onSignOut: () => Promise<void> }): ReactNode {
  const [error, setError] = useState<string | null>(null);

  const onSignOut = useCallback(async () => {
    setError(null);
    try {
      await props.onSignOut();
    } catch (e) {
      setError(friendlyError(e, "Could not sign out. Please try again."));
    }
  }, [props]);

  return (
    <article className="oa-account__card" aria-labelledby="oa-acct-signout">
      <h2 className="oa-account__card-title" id="oa-acct-signout">
        Sign out
      </h2>
      <p className="oa-account__card-note">End your session on this device.</p>
      <button
        type="button"
        className="oa-account__btn oa-account__btn--danger"
        disabled={props.signingOut}
        onClick={() => void onSignOut()}
      >
        {props.signingOut ? "Signing out…" : "Sign out"}
      </button>
      {error !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// Shared inline feedback
// -------------------------------------------------------------------------------------------------

function ActionFeedback(props: { feedback: Feedback }): ReactNode {
  const { feedback } = props;
  if (feedback.kind === "idle" || feedback.kind === "busy") return null;
  return (
    <p
      className="oa-account__feedback"
      data-kind={feedback.kind}
      role={feedback.kind === "error" ? "alert" : "status"}
      aria-live={feedback.kind === "error" ? undefined : "polite"}
    >
      {feedback.message}
    </p>
  );
}
